import { Button } from "primereact/button"
import { Divider } from "primereact/divider"
import { InputText } from "primereact/inputtext"
import { Password } from "primereact/password"
import { FC, useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import { ProgressSpinner } from "primereact/progressspinner"
import { AuthService } from "../../service/AuthService"
import { validateEmail, validatePassword } from "../../helpers/auth"
import { Plan } from "../../types/subscription"
import { SubscriptionService } from "../../service/SubscriptionService"

export interface IRegister {
  email: string
  password: string
}

const SETTINGS_URL = "/settings"

export const RegisterPage: FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [state, setState] = useState<IRegister>({
    email: "",
    password: ""
  })
  const [disableSubmitButton, setDisableSubmitButton] = useState<boolean>(true)
  const [isLoading, setIsLoading] = useState(false)
  const [planType, setPlanType] = useState<string>("")

  useEffect(() => {
    const subscriptionParam = searchParams.get("subscription")

    if (subscriptionParam) {
      const matchingPlan = Object.values(Plan).find(
        (plan) => plan.toLowerCase() === subscriptionParam.toLowerCase()
      )
      setPlanType(matchingPlan as Plan)
    }
  }, [searchParams])

  const onSubmit = async () => {
    setIsLoading(true)
    setDisableSubmitButton(true)

    await AuthService.register(state).then((response) => {
      if (response) {
        toast.success("User successfully created. Please sign in!")
      }
    })

    await AuthService.login(state).then(async (response) => {
      if (response) {
        localStorage.setItem("accessToken", response.AuthenticationResult?.IdToken)
      }

      if (planType) {
        const checkoutData = await SubscriptionService.activate({ plan_type: planType })
        if (checkoutData.url) {
          window.location.href = checkoutData.url
        }
      } else {
        navigate(SETTINGS_URL)
      }
    })

    setIsLoading(false)
    setDisableSubmitButton(false)
  }

  useEffect(() => {
    let isValid = true

    isValid = validateEmail(state.email) && validatePassword(state.password)

    setDisableSubmitButton(!isValid)
  }, [state.password, state.email])

  const header = <div className="font-bold mb-3">Pick a password</div>
  const footer = (
    <>
      <Divider />
      <p className="mt-2">Suggestions</p>
      <ul className="pl-2 ml-2 mt-0 line-height-3">
        <li>At least one lowercase</li>
        <li>At least one uppercase</li>
        <li>At least one numeric</li>
        <li>At least one special character</li>
      </ul>
    </>
  )

  return (
    <div className="relative flex flex-wrap align-items-center justify-content-center h-screen">
      <div className="vertical-align-middle border-transparent shadow-2 w-4  p-5">
        <h2 className="text-center">Register</h2>

        <div className="flex flex-column gap-2">
          <label htmlFor="email">Email</label>
          <InputText
            id="email-register"
            placeholder="Email"
            autoComplete="off"
            onChange={(e) => setState({ ...state, email: e.target.value as string })}
            value={state.email}
          />
        </div>
        <div className=" flex flex-column gap-2 mt-4 w-full">
          <label htmlFor="password">Password</label>
          <Password
            value={state.password}
            onChange={(e) => setState({ ...state, password: e.target.value as string })}
            placeholder="Password"
            toggleMask
            inputClassName="w-full"
            id="password-register"
            autoComplete="new-password"
            header={header}
            footer={footer}
          />
        </div>

        <div className="w-100 flex justify-content-center mt-4">
          <Button label="Submit" className="w-full" onClick={onSubmit} disabled={disableSubmitButton} />
        </div>
      </div>
      {isLoading && (
        <div className="absolute">
          <ProgressSpinner />
        </div>
      )}
    </div>
  )
}
